import { LegendState } from 'actions/legend/types/state';
import { Nullable } from '@tcc/common';
import { createUnit } from '../i18n/unitQtyWrapper';
import { getLayerUnits } from '../config/legendUnits';
import { LOCALE_GROUPS } from '../constants/units';
import { swapComma } from './swapComma';
import { Interval, IntervalWithStringValue, IntervalWithValue } from '../types/Legend';
import { CropsState } from '../actions/crops/types/state';

// This function checks to see if localized value needs to take into account crop nominal weight
// when process yield values
export function isLocaleGroupYield(localGroup: string): boolean {
  return [
    LOCALE_GROUPS.YIELD
  ].includes(localGroup);
}

export interface LocalizedValue {
  q: string;
  u: string,
  uSingular: string,
}

export interface UnitsParams {
  key?: string;
  localeGroup?: string;
  cropNominalWeight?: Nullable<number>;
  moisture?: number;
  dryMatterYieldUsage?: boolean;
  unitType?: string;
  agVizEnabled?: boolean;
  processSpecial?: string;
}

export interface OptionsParams {
  // eslint-disable-next-line @typescript-eslint/ban-types
  mathOperation?: (value: number) => number;
  cropCode?: string;
}

export function localizeUnits(value: number, units: UnitsParams, { mathOperation, cropCode }: OptionsParams = {}): LocalizedValue {
  let result = createUnit(value, units.key);
  // https://climate.jira.com/browse/YAN-722
  // NOTE | For crop yields, it is not guaranteed that units.cropNominalWeight will always exist.

  // the below ternary is complicated and not sure of it origins but basically adds the crop nominal weight
  // to the unit object to be used in downstream processing via i18n. This downstream processing breaks without
  // crop nominal weight supplied and throughout the react lifecycle the, see above comment too, the crop nominal
  // weight is not be there. The ternary in the condition check is the logic that keeps this from happening. 
  if (
    units.localeGroup &&
    (isLocaleGroupYield(units.localeGroup) ? units.cropNominalWeight : true)
  ) {
    result = result.forLocaleGroup(
      units.localeGroup,
      units.cropNominalWeight,
      mathOperation,
      { cropCode, moisture: units.moisture, dryMatterYieldUsage: units.dryMatterYieldUsage }
    );
  }

  return {
    q: result.scalarLocaleString,
    u: units.unitType
      ? result.label.quantitySpecific
      : result.symbol.quantitySpecific,
    uSingular: result.unitKey,
  };
}

interface LocalizeIntervalsReturnType {
  cropNominalWeight?: Nullable<number>,
  intervals: Interval[],
  key?: string;
  singularKey?: string;
}

export function localizeIntervals(intervals: Interval[], units: UnitsParams, { cropCode }: OptionsParams = {}): LocalizeIntervalsReturnType  {
  let key;
  let singularKey;
  const finalIntervals = intervals.map((interval, index) => {
    let mathOperation;
    if (index === 0) {
      mathOperation = Math.floor;
    }
    if (index === intervals.length - 1) {
      mathOperation = Math.ceil;
    }

    const localVal = localizeUnits('value' in interval ? Number(interval.value) : 0, units, {
      mathOperation,
      cropCode,
    });

    key = key || localVal.u;
    singularKey = singularKey || localVal.uSingular;

    return {
      ...interval,
      value: localVal.q,
    };
  });

  return {
    cropNominalWeight: units.cropNominalWeight,
    intervals: finalIntervals,
    key,
    singularKey,
  };
}

function localizeDates(
  intervals: IntervalWithStringValue[],
  locale?: string,
  agVizEnabled = false
): IntervalWithStringValue[] {
  const dateOpts: Intl.DateTimeFormatOptions = { month: '2-digit', year: 'numeric', day: '2-digit' };
  // The dates received from the backend are already localized when agViz is enabled,
  // so we should ignore the locale parameter. Basically we treat them as buckets,
  // because they are coverted to homestead (field) local date/time.

  if(agVizEnabled) {
    return intervals;
  }

  return intervals.map((interval) => ({
    ...interval,
    stringValue: new Date(interval.stringValue).toLocaleDateString(
      locale,
      dateOpts
    )
  }));
}


interface ProcessIntervalsParams {
  intervals?: Interval[];
  units?: UnitsParams;
  locale?: string;
  cropCode?: string;
  agVizEnabled?: boolean;
}

export type ProcessedIntervals = LocalizeIntervalsReturnType | { intervals: Interval[] };

export function isLocalizeIntervalsReturnType(processedIntervals: ProcessedIntervals): processedIntervals is LocalizeIntervalsReturnType {
  return 'key' in processedIntervals || 'singularKey' in processedIntervals || 'cropNominalWeight' in processedIntervals;
}

export function processIntervals({
  intervals = [],
  units = {},
  locale,
  cropCode,
  agVizEnabled = false
}: ProcessIntervalsParams): ProcessedIntervals {
  // If key is undefined, intervals are strings/numbers. No need for rounding or conversion.
  if (!units.key) {
    switch (units.processSpecial) {
      case 'date':
        return {
          intervals: localizeDates(intervals as IntervalWithStringValue[], locale, agVizEnabled),
        };

      default: {
        const valuesInterval = intervals as IntervalWithValue[];
        // There might be some cases like CEC, where no unit is associated to the measurement
        // but we still might want to allow the user edit the legend, for this purpose we need
        // the numbers are casted to "strings"
        return {
          intervals: valuesInterval.map(({ value, ...attrs }) => ({
            ...(value ? { value: String(value) } : {}),
            ...attrs,
          })) as Interval[],
        };
      }
    }
  }

  // If key is defined, convert intervals based on user's locale
  return localizeIntervals(intervals, units, { cropCode });
}


interface ConvertedLegendValues {
  max: number | string
  min: number | string;
  steps: Nullable<number>;
  unit: string 
}

export interface ConvertLegendValuesUnits {
  singularKey?: string,
  cropNominalWeight?: Nullable<number>;
  key?: string;
  /**
   * The unit the legend values must be converted back to before submitting.
   * For Ag-Viz (ARD) legends this is the legend's own native unit (e.g. `kg/ha`),
   * which can differ from the static layer default (e.g. `tonne/hectare` for yield).
   * When omitted, the layer's configured unit from `getLayerUnits` is used (legacy path).
   */
  baseUnitKey?: string;
}
/**
 * Convert legend values back to metric before they are sent in PUT request
 */
export function convertLegendValues(
  legend: LegendState,
  units: ConvertLegendValuesUnits,
  layer: string,
  localeGroup?: string
): ConvertedLegendValues {
  const { maxValue, minValue, steps } = legend;

  const formattedMax = Number.isNaN(Number(maxValue))
    ? swapComma(maxValue)
    : maxValue;
  const formattedMin = Number.isNaN(Number(minValue))
    ? swapComma(minValue)
    : minValue;

  // Determine the unit the values must be converted back to. Prefer the explicit
  // base unit (Ag-Viz legends carry their native unit, which may differ from the
  // static layer default), and fall back to the layer's configured unit otherwise.
  const expectedUnitKey = units.baseUnitKey || getLayerUnits(layer, localeGroup)?.key;
  if (expectedUnitKey) {
    const isExpectedUnit = units.singularKey === expectedUnitKey;

    const max = isExpectedUnit
      ? formattedMax
      : createUnit(formattedMax, units.singularKey).to(expectedUnitKey, units)
        .scalar;

    const min = isExpectedUnit
      ? formattedMin
      : createUnit(formattedMin, units.singularKey).to(expectedUnitKey, units)
        .scalar;

    return { max, min, steps, unit: expectedUnitKey };
  }

  // If there is no unit we return the raw numbers. This case might be triggered by
  // allowing the edit of a legend without units.
  return {
    max: formattedMax,
    min: formattedMin,
    steps,
    unit: '',
  };
}

export function getCropNominalWeight(crop?: string, cropList?: CropsState): number | null | undefined {
  const cropInfo = crop && cropList && crop in cropList ? cropList[crop] : undefined;
  if (cropInfo) {
    return (
      cropInfo.customNominalWeight ||
      (cropInfo['nominal-weight'] && cropInfo['nominal-weight'].q)
    );
  }

  return null;
}
